import {useEffect, useRef, useState} from 'react';
import {Navigate} from 'react-router-dom';
import WidgetsLoader from '@components/WidgetsLoader';
import {authService} from '@services/auth.service';
import type {PortalUser} from '../types';
import {canAccessClientPortal, requiresEmpresaSelection} from '../utils/permissions';

interface ClientPortalRouteProps {
    children: React.ReactNode;
}

/**
 * Protege rutas del portal de clientes.
 * Requiere JWT completo (tenant_id + empresa_id) tras POST /auth/select-empresa.
 */
const ClientPortalRoute = ({children}: ClientPortalRouteProps) => {
    const [sessionVersion, setSessionVersion] = useState(0);
    const [resolvingEmpresa, setResolvingEmpresa] = useState(false);
    const autoResolveAttempted = useRef(false);

    useEffect(() => {
        const handleSessionChange = () => {
            autoResolveAttempted.current = false;
            setSessionVersion(version => version + 1);
        };

        window.addEventListener('auth-session-changed', handleSessionChange);
        return () => window.removeEventListener('auth-session-changed', handleSessionChange);
    }, []);

    const token = authService.getToken();
    const user = authService.getUser() as PortalUser | null;
    const needsEmpresaSelection = requiresEmpresaSelection(user, token);

    useEffect(() => {
        if (
            autoResolveAttempted.current ||
            !authService.isAuthenticated() ||
            !canAccessClientPortal(user, token) ||
            !needsEmpresaSelection
        ) {
            return;
        }

        let cancelled = false;
        autoResolveAttempted.current = true;

        const resolveEmpresa = async () => {
            setResolvingEmpresa(true);

            try {
                await authService.ensureEmpresaContextSelected();
            } catch (error) {
                console.error('Error al resolver contexto de empresa:', error);
            } finally {
                if (!cancelled) {
                    setResolvingEmpresa(false);
                }
            }
        };

        resolveEmpresa();

        return () => {
            cancelled = true;
        };
    }, [needsEmpresaSelection, sessionVersion, user, token]);

    if (!authService.isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    if (!canAccessClientPortal(user, token)) {
        if (authService.hasImpersonationBackup()) {
            return <Navigate to="/select-clinic" replace />;
        }

        return <Navigate to="/login" replace />;
    }

    const stillNeedsEmpresaSelection = requiresEmpresaSelection(user, token);

    if (resolvingEmpresa && stillNeedsEmpresaSelection) {
        return <WidgetsLoader />;
    }

    if (stillNeedsEmpresaSelection) {
        if (authService.needsPortalEmpresaPicker(user as Record<string, unknown> | null, token)) {
            return <Navigate to="/login?portal_step=empresa" replace />;
        }

        return <Navigate to="/login?reason=empresa-context" replace />;
    }

    return <>{children}</>;
};

export default ClientPortalRoute;
